import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import InnerLayout from "../../components/layouts/inner-layout";
import SettingsItemContainer from "../../components/settings/settings-item-container";
import settingsData from "./settings-data";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DataContext } from "../../components/layouts/main-layout";

// Settings home: a sticky section sidebar on the left, a search box that
// filters the cards in place, and one card grid per section. Settings with an
// `input` component open inline below a breadcrumb; the rest navigate.
const Settings = () => {
  const { permissionFn } = useContext(DataContext);
  const navigate = useNavigate();
  const hasViewAccess = (moduleName) => {
    if (!moduleName) return true;
    const permission = permissionFn(moduleName);
    return permission?.read === true || permission?.all === true;
  };

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const searchRef = useRef(null);
  const sectionRefs = useRef({});

  // Sections the user can see, each with the items matching the search.
  // A section with no children is shown as a single card of itself.
  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (item) =>
      !q ||
      `${item?.title ?? ""} ${item?.desc ?? ""}`.toLowerCase().includes(q);
    return settingsData
      .map((section, index) => {
        const children = section?.childrens?.length
          ? section.childrens
          : [section];
        const items = children.filter(
          (item) => item && hasViewAccess(item?.module_name) && matches(item)
        );
        return { ...section, index, items };
      })
      .filter((section) => section.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, permissionFn]);

  const resultCount = sections.reduce((n, s) => n + s.items.length, 0);

  const handleItemClick = (index, setting, section) => {
    if (setting.input) {
      setSelectedComponent(setting.input);
      setSelectedSetting({ ...setting, sectionTitle: section?.title });
    } else {
      navigate(setting.path);
    }
  };

  const goBack = () => {
    setSelectedSetting(null);
    setSelectedComponent(null);
  };

  const scrollToSection = (index) => {
    setActiveIndex(index);
    sectionRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // "/" focuses search (unless already typing somewhere), Esc clears it.
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName);
      if (e.key === "/" && !typing && !selectedSetting) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedSetting]);

  // Highlight the sidebar entry of the section currently in view.
  useEffect(() => {
    if (selectedSetting) return;
    const root = document.querySelector(".innerLayoutScroller");
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveIndex(Number(visible[0].target.dataset.index));
      },
      { root, rootMargin: "0px 0px -60% 0px", threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [sections, selectedSetting]);

  // Old "#report-N" links still jump to that section.
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith("#report-")) {
        scrollToSection(Number(hash.replace("#report-", "")));
      }
    };
    window.addEventListener("hashchange", handleHashChange, false);
    handleHashChange();
    return () =>
      window.removeEventListener("hashchange", handleHashChange, false);
  }, []);

  // A setting opened inline: breadcrumb back to Settings + its component.
  if (selectedSetting) {
    return (
      <InnerLayout title="Settings">
        <div className="px-6 pt-5">
          <div className="flex items-center gap-1.5 text-sm">
            <button
              type="button"
              onClick={goBack}
              className="flex items-center gap-1.5 text-[var(--description-color)] hover:text-[var(--primary)]"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Settings
            </button>
            {selectedSetting.sectionTitle && (
              <>
                <span className="text-[var(--description-color)]">/</span>
                <span className="text-[var(--description-color)]">
                  {selectedSetting.sectionTitle}
                </span>
              </>
            )}
            <span className="text-[var(--description-color)]">/</span>
            <span className="fw_600 text-[var(--heading-color)]">
              {selectedSetting.title}
            </span>
          </div>
          <div className="w-full mt-4">{selectedComponent}</div>
        </div>
      </InnerLayout>
    );
  }

  return (
    <InnerLayout title="Settings">
      <div className="flex min-h-full bg-[#f8fafc]">
        {/* Section sidebar */}
        <aside className="hidden md:block w-60 shrink-0 border-r border-[var(--light-border-color)] bg-white">
          <nav className="sticky top-0 p-4">
            <div className="px-2 pb-3 text-[11px] fw_600 uppercase tracking-wider text-[var(--description-color)]">
              Sections
            </div>
            <ul className="flex flex-col gap-0.5">
              {sections.map((section) => {
                const active = section.index === activeIndex;
                return (
                  <li key={section.index}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(section.index)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                        active
                          ? "bg-[var(--row-selection-color)] text-[var(--primary)] fw_600"
                          : "text-[var(--heading-color)] hover:bg-[var(--row-hover-color)]"
                      }`}
                    >
                      <span className="truncate">{section.title}</span>
                      <span
                        className={`shrink-0 min-w-[1.5rem] h-5 px-1.5 rounded-full text-[11px] flex items-center justify-center ${
                          active
                            ? "bg-[var(--primary)] text-white"
                            : "bg-[#f1f5f9] text-[var(--description-color)]"
                        }`}
                      >
                        {section.items.length}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          {/* Header + search (sticky) */}
          <div className="sticky top-0 z-10 bg-[#f8fafc]/95 backdrop-blur px-6 lg:px-8 pt-6 pb-4 border-b border-[var(--light-border-color)]">
            <div className="w-full">
              <h1 className="text-xl fw_700 text-[var(--heading-color)]">
                Settings
              </h1>
              <p className="text-sm text-[var(--description-color)] mt-0.5">
                Configure your store, users, master data, discounts and
                accounts.
              </p>
              <div className="mt-4 flex items-center gap-2 h-11 px-3 rounded-xl border border-[var(--light-border-color)] bg-white focus-within:border-[var(--primary)] focus-within:ring-2 focus-within:ring-[var(--row-selection-color)] transition-shadow">
                <Search size={18} className="text-[var(--description-color)]" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setQuery("");
                      e.currentTarget.blur();
                    }
                  }}
                  placeholder="Search settings — e.g. tax, printer, users"
                  className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none placeholder:text-[var(--description-color)]"
                  type="text"
                />
                {query ? (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => {
                      setQuery("");
                      searchRef.current?.focus();
                    }}
                    className="p-1 rounded-md text-[var(--description-color)] hover:bg-[var(--row-hover-color)] hover:text-[var(--primary)]"
                  >
                    <X size={16} />
                  </button>
                ) : (
                  <kbd className="hidden sm:inline-flex items-center h-6 px-2 rounded-md border border-[var(--light-border-color)] text-[11px] text-[var(--description-color)] bg-[#f8fafc]">
                    /
                  </kbd>
                )}
              </div>
              {query && (
                <div className="mt-2 text-xs text-[var(--description-color)]">
                  {resultCount} result{resultCount === 1 ? "" : "s"} for “
                  {query.trim()}”
                </div>
              )}
            </div>
          </div>

          {/* Sections */}
          <div className="px-6 lg:px-8 py-6">
            <div className="w-full flex flex-col gap-10">
              {sections.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-20">
                  <div className="w-12 h-12 rounded-full bg-[var(--row-selection-color)] flex items-center justify-center">
                    <Search size={22} className="text-[var(--primary)]" />
                  </div>
                  <div className="mt-4 text-sm fw_600 text-[var(--heading-color)]">
                    No settings match “{query.trim()}”
                  </div>
                  <div className="mt-1 text-xs text-[var(--description-color)]">
                    Try a shorter word, like “tax” or “print”.
                  </div>
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="mt-4 text-sm fw_600 text-[var(--primary)] hover:underline"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                sections.map((section) => (
                  <section
                    key={section.index}
                    id={`report-${section.index}`}
                    data-index={section.index}
                    ref={(el) => (sectionRefs.current[section.index] = el)}
                    className="scroll-mt-44"
                  >
                    <div className="flex items-end justify-between gap-4 mb-3">
                      <div>
                        <h2 className="text-base fw_700 text-[var(--heading-color)]">
                          {section.title}
                        </h2>
                        {section.desc && (
                          <p className="text-xs text-[var(--description-color)] mt-0.5">
                            {section.desc}
                          </p>
                        )}
                      </div>
                      <span className="shrink-0 text-xs text-[var(--description-color)]">
                        {section.items.length} item
                        {section.items.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                      {section.items.map((item, itemIndex) => (
                        <SettingsItemContainer
                          key={`${section.index}-${item.title}`}
                          setting={item}
                          index={itemIndex}
                          query={query}
                          handleItemClick={(i, setting) =>
                            handleItemClick(i, setting, section)
                          }
                        />
                      ))}
                    </div>
                  </section>
                ))
              )}
            </div>
          </div>
        </main>
      </div>
    </InnerLayout>
  );
};

export default Settings;
